import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  Calendar, 
  Award, 
  Heart, 
  Activity, 
  Droplet, 
  Clock, 
  CheckCircle,
  TrendingUp,
  Users,
  AlertCircle,
  BarChart2,
  PieChart,
  RefreshCw
} from 'lucide-react';
import api from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import StatCard from './StatCard';
import ActivityTimeline from './ActivityTimeline';
import DonationChart from './DonationChart';
import BloodTypeDistribution from './BloodTypeDistribution';

const UserStatsDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUserStats();
  }, []);

  const fetchUserStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/dashboard/user-stats');
      console.log('User stats:', response.data);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching user statistics:', err);
      setError('Failed to load your statistics. Please try again.');
      toast.error('Failed to load your statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex flex-col items-center">
          <RefreshCw className="h-8 w-8 animate-spin text-red-500" />
          <p className="mt-2 text-gray-600">Loading your statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="h-8 w-8 text-red-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Something went wrong</h3>
        <p className="mt-2 text-sm text-gray-500">{error}</p>
        <button 
          onClick={fetchUserStats}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const { userInfo, donorStats, requesterStats, overallStats } = stats;

  return (
    <div className="space-y-6">
      {/* User Profile Overview */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center">
            <User className="w-12 h-12 text-red-600" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold text-gray-900">{userInfo.name}</h2>
            <p className="text-gray-500">{userInfo.email}</p>
            <div className="mt-2 flex flex-wrap gap-2 justify-center md:justify-start">
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                {userInfo.role.charAt(0).toUpperCase() + userInfo.role.slice(1)}
              </span>
              {userInfo.bloodGroup && (
                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                  Blood Group: {userInfo.bloodGroup}
                </span>
              )}
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                Member for {userInfo.daysActive} days
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {donorStats && (
          <>
            <StatCard 
              title="Total Donations"
              value={donorStats.totalDonations}
              icon={<Droplet className="w-6 h-6 text-red-500" />}
              description="Blood donations made"
              color="red"
            />
            <StatCard 
              title="Donation Streak"
              value={donorStats.donationStreak}
              icon={<Award className="w-6 h-6 text-amber-500" />}
              description="Consecutive donations"
              color="amber"
            />
            <StatCard 
              title="Lives Impacted"
              value={donorStats.totalUnitsDonated * 3}
              icon={<Heart className="w-6 h-6 text-pink-500" />}
              description="Estimated lives saved"
              color="pink"
            />
            <StatCard 
              title="Requests Fulfilled"
              value={donorStats.fulfilledRequests}
              icon={<CheckCircle className="w-6 h-6 text-green-500" />}
              description="Blood requests completed"
              color="green"
            />
          </>
        )}
        
        {requesterStats && (
          <>
            <StatCard 
              title="Total Requests"
              value={requesterStats.totalRequests}
              icon={<Activity className="w-6 h-6 text-blue-500" />}
              description="Blood requests created"
              color="blue"
            />
            <StatCard 
              title="Fulfilled Requests"
              value={requesterStats.fulfilledRequests}
              icon={<CheckCircle className="w-6 h-6 text-green-500" />}
              description="Requests completed"
              color="green"
            />
            <StatCard 
              title="Pending Requests"
              value={requesterStats.pendingRequests}
              icon={<Clock className="w-6 h-6 text-amber-500" />}
              description="Awaiting donors"
              color="amber"
            />
            <StatCard 
              title="Fulfillment Rate"
              value={`${requesterStats.fulfillmentRate}%`}
              icon={<TrendingUp className="w-6 h-6 text-indigo-500" />}
              description="Success rate"
              color="indigo"
            />
          </>
        )}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Donation History Chart */}
        {donorStats && donorStats.donationHistory && donorStats.donationHistory.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Donation History</h3>
            <div className="h-64">
              <DonationChart donationHistory={donorStats.donationHistory} />
            </div>
          </div>
        )}
        
        {/* Blood Type Distribution */}
        {overallStats && overallStats.bloodTypeDistribution && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Blood Type Distribution</h3>
            <div className="h-64">
              <BloodTypeDistribution distribution={overallStats.bloodTypeDistribution} />
            </div>
          </div>
        )}
      </div>

      {/* Activity Timeline */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
        <ActivityTimeline 
          donorActivity={donorStats?.recentActivity} 
          requesterActivity={requesterStats?.recentRequests}
        />
      </div>

      {/* Platform Comparison */}
      {overallStats && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Impact on the Platform</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                <span className="text-sm font-medium text-gray-700">Total Donors</span>
              </div>
              <p className="mt-2 text-2xl font-bold text-gray-900">{overallStats.totalDonors}</p>
              {donorStats && (
                <p className="text-xs text-gray-500 mt-1">
                  You are one of {overallStats.totalDonors} donors making a difference
                </p>
              )}
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-red-500" />
                <span className="text-sm font-medium text-gray-700">Total Requests</span>
              </div>
              <p className="mt-2 text-2xl font-bold text-gray-900">{overallStats.totalRequests}</p>
              {requesterStats && (
                <p className="text-xs text-gray-500 mt-1">
                  Your requests: {requesterStats.totalRequests} ({((requesterStats.totalRequests / overallStats.totalRequests) * 100).toFixed(1)}%)
                </p>
              )}
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium text-gray-700">Fulfilled Requests</span>
              </div>
              <p className="mt-2 text-2xl font-bold text-gray-900">{overallStats.fulfilledRequests}</p>
              {donorStats && (
                <p className="text-xs text-gray-500 mt-1">
                  Your contribution: {donorStats.fulfilledRequests} ({((donorStats.fulfilledRequests / overallStats.fulfilledRequests) * 100).toFixed(1)}%)
                </p>
              )}
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-500" />
                <span className="text-sm font-medium text-gray-700">Platform Success Rate</span>
              </div>
              <p className="mt-2 text-2xl font-bold text-gray-900">{overallStats.requestFulfillmentRate}%</p>
              {requesterStats && (
                <p className="text-xs text-gray-500 mt-1">
                  Your success rate: {requesterStats.fulfillmentRate}%
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserStatsDashboard;
